import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import MaterialReactTable, { type MRT_ColumnDef } from "material-react-table";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Field,
  Input,
  Textarea,
  Button,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
} from "@fluentui/react-components";
import {
  FolderFilled,
  DocumentTextRegular,
  MoreVerticalRegular,
  EditRegular,
  DeleteRegular,
  Copy20Regular,
  InfoRegular,
} from "@fluentui/react-icons";
import { AppDispatch } from "../../../../redux/store";
import {
  addFileLink,
  updateFileLink,
  deleteFileLink,
  FileLinkItem,
} from "../../../../redux/Document/fileLinkSlice";
import apiServices from "../../../../service";
import FormikInputRequest from "../../../../service/hook/useFormik";
import ASCButton from "../../../../components/Button";
import DynamicDatePicker from "../../../../components/ControlledDatePicker/dynamicDatepicker";
import "../style.css";
import { formatedDate } from "../../../../utils";

const PAGE_SIZE = 20;

function resolveType(fileLink: string): "file" | "folder" {
  return /[/\\]{1,2}$/.test(fileLink) ? "folder" : "file";
}

interface FileLinkPanelProps {
  activeCustomerId: string;
  notify: (msg: string, sub: string, type: string) => void;
}

// ── Delete confirmation dialog ────────────────────────────────────────────────
function DeleteConfirmDialog({
  item,
  onConfirm,
  onCancel,
}: {
  item: FileLinkItem | null;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  if (!item) return null;
  const typeLabel = item.type === "folder" ? "Folder" : "File";
  return (
    <Dialog open={!!item}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>Delete {typeLabel}?</DialogTitle>
        <DialogContent>
          Are you sure you want to delete <strong>{item.title}</strong>?
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={onCancel} label="No" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={onConfirm}
            className="asc-button-primary"
            label="Yes"
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ── Row actions menu ──────────────────────────────────────────────────────────
function RowActions({
  item,
  onEdit,
  onDelete,
}: {
  item: FileLinkItem;
  onEdit: (item: FileLinkItem) => void;
  onDelete: (item: FileLinkItem) => void;
}) {
  return (
    <Menu positioning="below-end">
      <MenuTrigger>
        <Button appearance="subtle" icon={<MoreVerticalRegular />} />
      </MenuTrigger>
      <MenuPopover>
        <MenuList>
          <MenuItem icon={<EditRegular />} onClick={() => onEdit(item)}>
            Edit
          </MenuItem>
          <MenuItem icon={<DeleteRegular />} onClick={() => onDelete(item)}>
            Delete
          </MenuItem>
        </MenuList>
      </MenuPopover>
    </Menu>
  );
}

// ── Add / Edit dialog ─────────────────────────────────────────────────────────
function FileLinkDialog({
  isOpen,
  editItem,
  mode,
  activeCustomerId,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  editItem: FileLinkItem | null;
  mode: "folder" | "file";
  activeCustomerId: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const dispatch = useDispatch<AppDispatch>();

  const formik = FormikInputRequest({
    initialValues: {
      title: "",
      description: "",
      fileLink: "",
      createdAt: new Date(),
    },
    enableReinitialize: true,
    onSubmitFn: async (values: any) => {
      const payload = {
        documentTitle: values.title,
        description: values.description,
        fileLink: values.fileLink,
        customer_id: activeCustomerId,
        itemType: mode,
        createdAt: values.createdAt,
      };
      if (editItem) {
        await dispatch(updateFileLink({ id: editItem.id, data: payload }));
      } else {
        await dispatch(addFileLink({ data: payload }));
      }
      onSaved();
      onClose();
      formik.resetForm();
    },
    validate: (values: any) => {
      const errors: any = {};
      if (!values.fileLink) errors.fileLink = "File path is required";
      return errors;
    },
  });

  useEffect(() => {
    formik.resetForm({
      values: {
        title: editItem?.title ?? "",
        description: editItem?.description ?? "",
        fileLink: editItem?.fileLink ?? "",
        createdAt: (editItem as any)?.createdAt
          ? new Date((editItem as any).createdAt)
          : new Date(),
      },
    });
  }, [editItem, isOpen]);

  const handleCancel = () => {
    onClose();
    formik.resetForm();
  };

  const dialogTitle = editItem
    ? `Edit ${editItem.type === "folder" ? "Folder" : "File"} Link`
    : mode === "folder"
      ? "Add Folder Link"
      : "Add File Link";

  return (
    <Dialog open={isOpen}>
      <DialogSurface className="dialog-surface">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <div className="folder_doc">
            <Field label={mode === "folder" ? "Folder Name" : "File Name"}>
              <Input
                appearance="outline"
                value={formik.values.title}
                onChange={(e) => formik.setFieldValue("title", e.target.value)}
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field
              label={
                <span className="filelink-label">
                  File Path
                  {formik.errors.fileLink && (
                    <span className="required-item">*</span>
                  )}
                </span>
              }
              validationState={formik.errors.fileLink ? "error" : "none"}
              validationMessage={`${formik.errors.fileLink || ""}`}
            >
              <Input
                appearance="outline"
                placeholder={
                  mode === "folder"
                    ? "e.g. C:\\Users\\John\\Documents\\ or /home/user/documents/"
                    : "e.g. C:\\Users\\John\\Documents\\file.pdf or file:///home/user/file.pdf"
                }
                value={formik.values.fileLink}
                onChange={(e) =>
                  formik.setFieldValue("fileLink", e.target.value)
                }
                contentAfter={
                  <Tooltip
                    content="This stores the reference path of the target file or folder. To access it, copy the path and paste it into a new browser tab. The corresponding file or directory will open."
                    relationship="description"
                    positioning="below-end"
                  >
                    <InfoRegular style={{ cursor: "help" }} />
                  </Tooltip>
                }
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field label="Date">
              <DynamicDatePicker
                formik={formik}
                border
                name="createdAt"
                customHandleChange={(e: any) => {
                  formik.setFieldValue(e.target.name, e.target.value);
                }}
              />
            </Field>
          </div>
          <div className="folder_doc">
            <Field label="Description">
              <Textarea
                appearance="outline"
                style={{ backgroundColor: "#fff" }}
                value={formik.values.description}
                onChange={(e) =>
                  formik.setFieldValue("description", e.target.value)
                }
              />
            </Field>
          </div>
        </DialogContent>
        <DialogActions className="flex-end">
          <ASCButton shape="rounded" onClick={handleCancel} label="Cancel" />
          <ASCButton
            shape="rounded"
            appearance="primary"
            onClick={() => formik.handleSubmit()}
            className="asc-button-primary"
            label="Save"
          />
        </DialogActions>
      </DialogSurface>
    </Dialog>
  );
}

// ── Path cell with copy ───────────────────────────────────────────────────────
function PathCell({
  path,
  notify,
}: {
  path: string;
  notify: (msg: string, sub: string, type: string) => void;
}) {
  const handleCopy = () => {
    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(path).then(() => {
        notify("Path copied to clipboard", "", "success");
      });
    } else {
      const textarea = document.createElement("textarea");
      textarea.value = path;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      notify("Path copied to clipboard", "", "success");
    }
  };

  return (
    <span className="filelink-path-cell">
      <span className="doc-path-cell">{path}</span>
      <Tooltip content="Copy path" relationship="label">
        <Copy20Regular className="filelink-copy-icon" onClick={handleCopy} />
      </Tooltip>
    </span>
  );
}

// ── Main panel ────────────────────────────────────────────────────────────────
export default function FileLinkPanel({
  activeCustomerId,
  notify,
}: FileLinkPanelProps) {
  const dispatch = useDispatch<AppDispatch>();

  const [data, setData] = useState<FileLinkItem[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const offsetRef = useRef(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<"folder" | "file">("file");
  const [editItem, setEditItem] = useState<FileLinkItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<FileLinkItem | null>(null);

  const fetchPage = useCallback(async (offset: number, append: boolean) => {
    setIsFetching(true);
    try {
      const res = await apiServices.fileLink.getList(activeCustomerId, offset, PAGE_SIZE);
      const raw: any[] = res.data.data ?? [];
      const items: FileLinkItem[] = raw.map((item) => ({
        ...item,
        type: resolveType(item.fileLink),
      }));
      const total: number = res.data.pagination?.total ?? items.length;

      setData((prev) => {
        const combined = append ? [...prev, ...items] : items;
        combined.sort((a, b) => {
          if (a.type !== b.type) return a.type === "folder" ? -1 : 1;
          return (a.title || "").localeCompare(b.title || "");
        });
        return combined;
      });
      offsetRef.current = offset + items.length;
      setHasMore(offsetRef.current < total);
    } finally {
      setIsFetching(false);
    }
  }, [activeCustomerId]);

  // Initial load / reset
  const resetAndFetch = useCallback(() => {
    offsetRef.current = 0;
    setHasMore(true);
    fetchPage(0, false);
  }, [fetchPage]);

  useEffect(() => {
    resetAndFetch();
  }, [activeCustomerId]);

  const fetchMore = useCallback(() => {
    if (!isFetching && hasMore) {
      fetchPage(offsetRef.current, true);
    }
  }, [isFetching, hasMore, fetchPage]);

  const handleScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollHeight, scrollTop, clientHeight } = event.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      fetchMore();
    }
  }, [fetchMore]);

  const handleEdit = (item: FileLinkItem) => {
    setEditItem(item);
    setDialogMode(item.type);
    setDialogOpen(true);
  };

  const openAddDialog = (mode: "folder" | "file") => {
    setEditItem(null);
    setDialogMode(mode);
    setDialogOpen(true);
  };

  const handleDelete = (item: FileLinkItem) => {
    setDeleteItem(item);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;
    await dispatch(deleteFileLink(deleteItem.id));
    notify("File link deleted", "", "success");
    setDeleteItem(null);
    resetAndFetch();
  };

  const handleSaved = () => {
    notify(editItem ? "File link updated" : "File link added", "", "success");
    resetAndFetch();
  };

  const filteredData = useMemo(() => {
    if (!search.trim()) return data;
    const lower = search.toLowerCase();
    return data.filter((item) => item.title?.toLowerCase().includes(lower));
  }, [data, search]);

  const columns = useMemo<MRT_ColumnDef<FileLinkItem>[]>(
    () => [
      {
        accessorKey: "title",
        header: "Name",
        size: 200,
        Cell: ({ row }) => (
          <div className="doc-name-cell" title={row.original.fileLink}>
            {row.original.type === "folder" ? (
              <FolderFilled primaryFill="#fccc77" fontSize="20px" />
            ) : (
              <DocumentTextRegular primaryFill="#8c8c8c" fontSize="20px" />
            )}
            <span className="doc-item-name">{row.original.title}</span>
          </div>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 80,
        Cell: ({ row }) => (row.original.type === "folder" ? "Folder" : "File"),
      },
      {
        accessorKey: "created_at",
        header: "Date",
        size: 80,
        Cell: ({ row }) => (formatedDate(row.original?.created_at) || "—"),
      },
      {
        accessorKey: "fileLink",
        header: "Path",
        Cell: ({ row }) => (
          <PathCell path={row.original.fileLink} notify={notify} />
        ),
      },
      {
        accessorKey: "description",
        header: "Description",
        Cell: ({ row }) => <span>{row.original.description || "—"}</span>,
      },
      {
        id: "actions",
        header: "Actions",
        size: 90,
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <RowActions
            item={row.original}
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        ),
      },
    ],
    [],
  );

  return (
    <>
      <div className="doc-mgmt-toolbar">
        <Input
          appearance="outline"
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          contentAfter={search ? (
            <span
              style={{ cursor: "pointer", fontSize: 12, color: "#888" }}
              onClick={() => setSearch("")}
            >✕</span>
          ) : undefined}
          style={{ minWidth: 220 }}
        />
        <div className="doc-actions-bar">
          <ASCButton
            shape="rounded"
            appearance="secondary"
            onClick={() => openAddDialog("folder")}
            label="Add Folder"
          />
          <ASCButton
            shape="rounded"
            appearance="primary"
            className="asc-button-primary"
            onClick={() => openAddDialog("file")}
            label="Add File Link"
          />
        </div>
      </div>

      <div className="doc-mgmt-table-wrapper">
        <MaterialReactTable
          columns={columns}
          data={filteredData}
          enableTopToolbar={false}
          enableBottomToolbar={false}
          enableColumnActions={false}
          enableSorting={true}
          enableFilters={false}
          enablePagination={false}
          enableColumnResizing={false}
          enableStickyHeader={true}
          enableRowSelection={false}
          state={{ isLoading: isFetching && data.length === 0, showProgressBars: isFetching && data.length > 0 }}
          muiTableContainerProps={{
            ref: tableContainerRef,
            sx: { maxHeight: "calc(100vh - 300px)", overflow: "auto" },
            onScroll: handleScroll,
          }}
          muiTableHeadCellProps={{
            sx: {
              fontWeight: 600,
              fontSize: "1rem",
              backgroundColor: "#f9f9f9",
              borderBottom: "1px solid #ddd",
              position: "sticky",
              top: 0,
              zIndex: 1,
            },
          }}
          muiTableBodyRowProps={{
            sx: { "&:hover td": { backgroundColor: "#f5f5f5" } },
          }}
          muiTableBodyCellProps={{ sx: { fontSize: "1rem" } }}
          localization={{ noRecordsToDisplay: "No file links found" }}
          initialState={{ density: "compact" }}
        />
      </div>

      <FileLinkDialog
        isOpen={dialogOpen}
        editItem={editItem}
        mode={dialogMode}
        activeCustomerId={activeCustomerId}
        onClose={() => setDialogOpen(false)}
        onSaved={handleSaved}
      />

      <DeleteConfirmDialog
        item={deleteItem}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteItem(null)}
      />
    </>
  );
}
